import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { condicionesIva } from "utils/condicionesAfip";

const PROVINCIAS_QUERY = gql`
  query provinciasVoucherCliente {
    provincias {
      id
      nombre
    }
  }
`;

const LOCALIDADES_QUERY = gql`
  query localidadesVoucherCliente($provincia: ID!) {
    localidades(provincia: $provincia) {
      id
      nombre
    }
  }
`;

const SAVE_VOUCHER_CLIENTE = gql`
  mutation saveVoucherClienteDesdeGrupo($input: VoucherClienteInput!) {
    saveVoucherCliente(input: $input) {
      id
      tipoCliente
      nombre
      apellido
      razonSocial
      tipoDocumento
      numeroDocumento
      condicionIva
      condicionIVAnombre
      cuit
      idProvincia
      idLocalidad
      telefono
      email
      calle
      numero
      codigoPostal
      provincia {
        id
        nombre
      }
      localidad {
        id
        nombre
      }
    }
  }
`;

const EMPTY_FORM = {
  tipoCliente: "PERSONA",
  nombre: "",
  apellido: "",
  razonSocial: "",
  tipoDocumento: 96,
  numeroDocumento: "",
  condicionIva: 5,
  condicionIVAnombre: "Consumidor Final",
  cuit: "",
  idProvincia: "",
  idLocalidad: "",
  telefono: "",
  email: "",
  calle: "",
  numero: "",
  codigoPostal: "",
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "94%", sm: "92%", md: "90%", lg: "82%", xl: "72%" },
  maxWidth: "1120px",
  maxHeight: "90vh",
  overflowY: "auto",
  p: 0,
  borderRadius: "16px",
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
  outline: "none",
};

const sectionStyle = {
  height: "100%",
  p: { xs: 2, sm: 2.25 },
  border: "1px solid #E2E8F0",
  borderRadius: "12px",
  backgroundColor: "#FFFFFF",
};

const fiscalTextColor = "#111827";

const digits = (value) => String(value || "").replace(/\D/g, "");

const getMutationErrorMessage = (resultOrError) => {
  const errors = [
    ...(Array.isArray(resultOrError?.errors) ? resultOrError.errors : []),
    ...(Array.isArray(resultOrError?.graphQLErrors) ? resultOrError.graphQLErrors : []),
    ...(Array.isArray(resultOrError?.networkError?.result?.errors)
      ? resultOrError.networkError.result.errors
      : []),
  ];
  const messages = errors
    .map((error) => (typeof error === "string" ? error : error?.message))
    .filter(Boolean);

  if (messages.length > 0) return [...new Set(messages)].join(". ");

  return resultOrError?.message || "";
};

export default function VoucherClienteDialog({ open, onClose, onSaved }) {
  const { handleSnackbar } = useContext(MessageManager);
  const firstFieldRef = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const { data: provinciasData } = useQuery(PROVINCIAS_QUERY, { skip: !open });
  const [loadLocalidades, { data: localidadesData, loading: loadingLocalidades }] =
    useLazyQuery(LOCALIDADES_QUERY);
  const [saveVoucherCliente, { loading }] = useMutation(SAVE_VOUCHER_CLIENTE, {
    errorPolicy: "all",
  });

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setTimeout(() => firstFieldRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    if (form.idProvincia) loadLocalidades({ variables: { provincia: form.idProvincia } });
  }, [form.idProvincia, loadLocalidades]);

  const localidades = localidadesData?.localidades || [];
  const isEmpresa = form.tipoCliente === "EMPRESA";
  const isCuitDocument = Number(form.tipoDocumento) === 80;
  const valid = useMemo(() => {
    const identityValid = isEmpresa
      ? Boolean(form.razonSocial.trim())
      : Boolean(form.nombre.trim() && form.apellido.trim());
    const documentValid = isEmpresa
      ? true
      : Boolean(form.tipoDocumento) && (isCuitDocument || Boolean(digits(form.numeroDocumento)));
    return (
      identityValid &&
      documentValid &&
      Boolean(form.condicionIva) &&
      digits(form.cuit).length === 11 &&
      Boolean(form.idProvincia && form.idLocalidad) &&
      Boolean(form.calle.trim() && form.numero.trim() && form.codigoPostal.trim()) &&
      (!form.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    );
  }, [form, isCuitDocument, isEmpresa]);

  const change = (field) => (event) => {
    const value = event.target.value;
    setForm((previous) => ({
      ...previous,
      [field]: value,
      ...(field === "condicionIva"
        ? {
            condicionIVAnombre:
              condicionesIva.find((item) => Number(item.id) === Number(value))?.nombre || "",
          }
        : {}),
      ...(field === "idProvincia" ? { idLocalidad: "" } : {}),
      ...(field === "tipoCliente" && value === "EMPRESA"
        ? {
            nombre: "",
            apellido: "",
            tipoDocumento: 80,
            numeroDocumento: digits(previous.cuit),
          }
        : {}),
      ...(field === "tipoCliente" && value === "PERSONA"
        ? { razonSocial: "", tipoDocumento: 96, numeroDocumento: "" }
        : {}),
      ...(field === "cuit" &&
      (previous.tipoCliente === "EMPRESA" || Number(previous.tipoDocumento) === 80)
        ? { tipoDocumento: 80, numeroDocumento: digits(value) }
        : {}),
      ...(field === "tipoDocumento"
        ? { numeroDocumento: Number(value) === 80 ? digits(previous.cuit) : "" }
        : {}),
    }));
  };

  const handleSave = async () => {
    if (!valid) return;
    try {
      const cuit = digits(form.cuit);
      const input = {
        ...form,
        tipoDocumento: isEmpresa ? 80 : Number(form.tipoDocumento),
        condicionIva: Number(form.condicionIva),
        cuit,
        numeroDocumento: isEmpresa || isCuitDocument ? cuit : digits(form.numeroDocumento),
        idProvincia: Number(form.idProvincia),
        idLocalidad: Number(form.idLocalidad),
        telefono: form.telefono.trim() || null,
        email: form.email.trim() || null,
      };
      const response = await saveVoucherCliente({ variables: { input } });
      const mutationErrorMessage = getMutationErrorMessage(response);
      if (mutationErrorMessage) {
        handleSnackbar(mutationErrorMessage, "error");
        return;
      }

      const created = response?.data?.saveVoucherCliente;
      if (!created) {
        handleSnackbar("No se pudo guardar el cliente facturable", "error");
        return;
      }

      handleSnackbar("Cliente facturable guardado correctamente", "success");
      onSaved(created);
      onClose();
    } catch (error) {
      handleSnackbar(
        getMutationErrorMessage(error) || "No se pudo guardar el cliente facturable",
        "error"
      );
    }
  };

  const renderLabel = (id, text, required = false, textColor = "#344767") => (
    <InputLabel htmlFor={id} sx={{ mb: 0.6, lineHeight: 1.2 }}>
      <SoftTypography
        component="label"
        variant="caption"
        fontWeight="bold"
        sx={{ color: textColor }}
      >
        {text}
        {required ? (
          <SoftTypography
            component="span"
            variant="caption"
            fontWeight="bold"
            sx={{ color: "#F0057A" }}
          >
            &nbsp;*
          </SoftTypography>
        ) : null}
      </SoftTypography>
    </InputLabel>
  );

  const renderInput = (name, placeholder, props = {}) => (
    <SoftInput
      id={`voucher-cliente-${name}`}
      name={name}
      placeholder={placeholder}
      value={form[name]}
      onChange={change(name)}
      {...props}
    />
  );

  return (
    <Modal
      aria-labelledby="voucher-cliente-title"
      aria-describedby="voucher-cliente-description"
      open={open}
      onClose={loading ? undefined : onClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={modalStyle}>
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={2}
            px={{ xs: 2, sm: 3 }}
            pt={{ xs: 2, sm: 2.75 }}
            pb={2}
          >
            <SoftBox>
              <SoftTypography id="voucher-cliente-title" variant="h5" fontWeight="bold">
                Agregar cliente facturable
              </SoftTypography>
              <SoftTypography
                id="voucher-cliente-description"
                variant="caption"
                color="secondary"
                display="block"
                mt={0.5}
              >
                Completa la información del cliente. Los campos marcados con
                <SoftTypography
                  component="span"
                  variant="caption"
                  fontWeight="bold"
                  sx={{ color: "#F0057A" }}
                >
                  &nbsp;*
                </SoftTypography>
                &nbsp;son obligatorios.
              </SoftTypography>
            </SoftBox>
            <Tooltip title="Cerrar" placement="top">
              <span>
                <SoftButton
                  color="white"
                  circular
                  iconOnly
                  onClick={onClose}
                  disabled={loading}
                  aria-label="Cerrar alta de cliente facturable"
                  sx={{ minWidth: 34, width: 34, height: 34, p: 0, boxShadow: "none" }}
                >
                  <Icon>close</Icon>
                </SoftButton>
              </span>
            </Tooltip>
          </SoftBox>

          <SoftBox px={{ xs: 2, sm: 3 }} pb={{ xs: 2, sm: 3 }}>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={5}>
                <SoftBox
                  component="section"
                  aria-labelledby="voucher-cliente-general-title"
                  sx={sectionStyle}
                >
                  <SoftBox display="flex" alignItems="center" gap={1.25} mb={2.25}>
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width={42}
                      height={42}
                      borderRadius="50%"
                      sx={{ flexShrink: 0, color: "#2563EB", backgroundColor: "#EFF6FF" }}
                    >
                      <Icon>person_outline</Icon>
                    </SoftBox>
                    <SoftBox>
                      <SoftTypography
                        id="voucher-cliente-general-title"
                        variant="button"
                        fontWeight="bold"
                        sx={{ color: "#2563EB" }}
                      >
                        Datos generales
                      </SoftTypography>
                      <SoftTypography variant="caption" color="secondary" display="block">
                        Información principal y de contacto del cliente.
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {renderLabel("voucher-cliente-tipoCliente", "Persona / empresa", true)}
                      <Select
                        id="voucher-cliente-tipoCliente"
                        name="tipoCliente"
                        value={form.tipoCliente}
                        onChange={change("tipoCliente")}
                        inputRef={firstFieldRef}
                        fullWidth
                      >
                        <MenuItem value="PERSONA">Persona</MenuItem>
                        <MenuItem value="EMPRESA">Empresa</MenuItem>
                      </Select>
                    </Grid>

                    {isEmpresa ? (
                      <Grid item xs={12}>
                        {renderLabel("voucher-cliente-razonSocial", "Razón social", true)}
                        {renderInput("razonSocial", "Ingrese la razón social")}
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={12} sm={6}>
                          {renderLabel("voucher-cliente-nombre", "Nombre", true)}
                          {renderInput("nombre", "Ingrese el nombre")}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          {renderLabel("voucher-cliente-apellido", "Apellido", true)}
                          {renderInput("apellido", "Ingrese el apellido")}
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} sm={6}>
                      {renderLabel("voucher-cliente-email", "Email")}
                      {renderInput("email", "cliente@correo.com", {
                        type: "email",
                        icon: { component: <Icon>email</Icon>, direction: "left" },
                      })}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderLabel("voucher-cliente-telefono", "Teléfono")}
                      {renderInput("telefono", "Ingrese el teléfono", {
                        icon: { component: <Icon>phone</Icon>, direction: "left" },
                      })}
                    </Grid>
                  </Grid>
                </SoftBox>
              </Grid>

              <Grid item xs={12} md={7}>
                <SoftBox
                  component="section"
                  aria-labelledby="voucher-cliente-fiscal-title"
                  sx={{ ...sectionStyle, color: fiscalTextColor }}
                >
                  <SoftBox display="flex" alignItems="center" gap={1.25} mb={2.25}>
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width={42}
                      height={42}
                      borderRadius="50%"
                      sx={{ flexShrink: 0, color: "#059669", backgroundColor: "#ECFDF5" }}
                    >
                      <Icon>account_balance</Icon>
                    </SoftBox>
                    <SoftBox>
                      <SoftTypography
                        id="voucher-cliente-fiscal-title"
                        variant="button"
                        fontWeight="bold"
                        sx={{ color: fiscalTextColor }}
                      >
                        Datos fiscales
                      </SoftTypography>
                      <SoftTypography
                        variant="caption"
                        display="block"
                        sx={{ color: fiscalTextColor }}
                      >
                        Información fiscal y domicilio del cliente.
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      {renderLabel(
                        "voucher-cliente-tipoDocumento",
                        "Tipo de documento",
                        true,
                        fiscalTextColor
                      )}
                      <Select
                        id="voucher-cliente-tipoDocumento"
                        name="tipoDocumento"
                        value={form.tipoDocumento}
                        onChange={change("tipoDocumento")}
                        disabled={isEmpresa}
                        fullWidth
                        sx={{ color: fiscalTextColor }}
                      >
                        <MenuItem value={80}>CUIT</MenuItem>
                        <MenuItem value={96}>DNI</MenuItem>
                        <MenuItem value={86}>CUIL</MenuItem>
                      </Select>
                      {isEmpresa ? (
                        <SoftTypography
                          variant="caption"
                          display="block"
                          mt={0.5}
                          sx={{ color: fiscalTextColor }}
                        >
                          Para empresas se utiliza CUIT.
                        </SoftTypography>
                      ) : isCuitDocument ? (
                        <SoftTypography
                          variant="caption"
                          display="block"
                          mt={0.5}
                          sx={{ color: fiscalTextColor }}
                        >
                          Se utilizará el CUIT informado para facturación.
                        </SoftTypography>
                      ) : null}
                    </Grid>

                    {!isEmpresa && !isCuitDocument ? (
                      <Grid item xs={12} sm={6}>
                        {renderLabel(
                          "voucher-cliente-numeroDocumento",
                          "Número de documento",
                          true,
                          fiscalTextColor
                        )}
                        {renderInput("numeroDocumento", "Ingrese el número", {
                          inputProps: { inputMode: "numeric" },
                          sx: { color: fiscalTextColor },
                        })}
                      </Grid>
                    ) : null}

                    <Grid item xs={12} sm={6}>
                      {renderLabel("voucher-cliente-cuit", "CUIT", true, fiscalTextColor)}
                      {renderInput("cuit", "11 dígitos, sin guiones", {
                        inputProps: {
                          inputMode: "numeric",
                          maxLength: 11,
                          "aria-describedby": "voucher-cliente-cuit-help",
                        },
                        sx: { color: fiscalTextColor },
                      })}
                      <SoftTypography
                        id="voucher-cliente-cuit-help"
                        variant="caption"
                        display="block"
                        mt={0.5}
                        sx={{ color: fiscalTextColor }}
                      >
                        Ingrese únicamente números.
                      </SoftTypography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      {renderLabel(
                        "voucher-cliente-condicionIva",
                        "Condición frente al IVA",
                        true,
                        fiscalTextColor
                      )}
                      <Select
                        id="voucher-cliente-condicionIva"
                        name="condicionIva"
                        value={form.condicionIva}
                        onChange={change("condicionIva")}
                        fullWidth
                        sx={{ color: fiscalTextColor }}
                      >
                        {condicionesIva.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      {renderLabel(
                        "voucher-cliente-idProvincia",
                        "Provincia",
                        true,
                        fiscalTextColor
                      )}
                      <Select
                        id="voucher-cliente-idProvincia"
                        name="idProvincia"
                        value={form.idProvincia}
                        onChange={change("idProvincia")}
                        fullWidth
                        displayEmpty
                        sx={{ color: fiscalTextColor }}
                      >
                        <MenuItem value="" disabled>
                          <em>Seleccione</em>
                        </MenuItem>
                        {(provinciasData?.provincias || []).map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      {renderLabel(
                        "voucher-cliente-idLocalidad",
                        "Localidad",
                        true,
                        fiscalTextColor
                      )}
                      <Select
                        id="voucher-cliente-idLocalidad"
                        name="idLocalidad"
                        value={form.idLocalidad}
                        onChange={change("idLocalidad")}
                        fullWidth
                        disabled={!form.idProvincia || loadingLocalidades}
                        displayEmpty
                        sx={{ color: fiscalTextColor }}
                      >
                        <MenuItem value="" disabled>
                          <em>{loadingLocalidades ? "Cargando..." : "Seleccione"}</em>
                        </MenuItem>
                        {localidades.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      {renderLabel("voucher-cliente-calle", "Calle", true, fiscalTextColor)}
                      {renderInput("calle", "Ingrese la calle", { sx: { color: fiscalTextColor } })}
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      {renderLabel("voucher-cliente-numero", "Número", true, fiscalTextColor)}
                      {renderInput("numero", "Altura", { sx: { color: fiscalTextColor } })}
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      {renderLabel(
                        "voucher-cliente-codigoPostal",
                        "Código postal",
                        true,
                        fiscalTextColor
                      )}
                      {renderInput("codigoPostal", "Código postal", {
                        sx: { color: fiscalTextColor },
                      })}
                    </Grid>
                  </Grid>
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>

          <SoftBox
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={1.5}
            flexWrap="wrap"
            px={{ xs: 2, sm: 3 }}
            py={1.75}
            sx={{ borderTop: "1px solid #E2E8F0", backgroundColor: "#FFFFFF" }}
          >
            <SoftButton
              color="secondary"
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              sx={{ borderRadius: "8px", px: 2.25 }}
            >
              Cancelar
            </SoftButton>
            <SoftButton
              color="uproGreen"
              onClick={handleSave}
              disabled={!valid || loading}
              sx={{ borderRadius: "8px", px: 2.25 }}
            >
              {loading ? (
                <CircularProgress size={15} color="inherit" />
              ) : (
                <Icon sx={{ fontWeight: "light" }}>save</Icon>
              )}
              &nbsp;{loading ? "Guardando..." : "Guardar cliente"}
            </SoftButton>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

VoucherClienteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
